/**
 * Vista user/admin/account, reporte abierto, goHome y detalle de aprobación.
 * Salir del Creador con cambios sin guardar pide confirmación (leaveGuard).
 */
import React, { createContext, useState, useCallback, useContext, useRef } from 'react';
import { useData } from './DataContext';
import { isCustomBuiltReport, customReportMarker } from './reportViewUtils';
import { confirmLeaveCreator } from './components/admin/widget/leaveGuard';

const CREATOR_SUBVIEW = 'creador-reportes';

const NavigationContext = createContext();

export const useNavigation = () => useContext(NavigationContext);

export const NavigationProvider = ({ children }) => {
    const { dashboards, pendingApprovalReports } = useData();
    const [view, setViewState] = useState('user');
    const [adminSubView, setAdminSubViewState] = useState('biblioteca');
    const inCreatorRef = useRef(false);
    inCreatorRef.current = view === 'admin' && adminSubView === CREATOR_SUBVIEW;
    /** false si el usuario decidió quedarse en el Creador para no perder cambios. */
    const canLeaveCreator = () => !inCreatorRef.current || confirmLeaveCreator();

    const setView = useCallback((next) => {
        if (typeof next !== 'function' && next !== 'admin' && !canLeaveCreator()) return;
        setViewState(next);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- canLeaveCreator lee refs
    const setAdminSubView = useCallback((next) => {
        if (typeof next !== 'function' && next !== CREATOR_SUBVIEW && !canLeaveCreator()) return;
        setAdminSubViewState(next);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- canLeaveCreator lee refs
    const [selectedUrl, setSelectedUrlState] = useState(null);
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [selectedReportForDetails, setSelectedReportForDetails] = useState(null);

    const setSelectedUrl = useCallback((url) => {
        setSelectedUrlState(url);
        if (!url) setSelectedReportId(null);
    }, []);

    const openReport = useCallback((report) => {
        if (!canLeaveCreator()) return;
        if (!report) {
            setSelectedReportId(null);
            setSelectedUrlState(null);
            setView('user');
            return;
        }
        setSelectedReportId(report.id || null);
        setSelectedUrlState(
            isCustomBuiltReport(report)
                ? customReportMarker(report.id)
                : (report.dashboardUrl || null)
        );
        setView('user');
    }, []);

    const handleViewReportDetailsById = useCallback((reportId) => {
        if (!reportId) return;
        if (!canLeaveCreator()) return;
        const allAvailableReports = [...dashboards, ...pendingApprovalReports];
        const report = allAvailableReports.find(r => r.id === reportId);

        if (report) {
            setSelectedReportForDetails(report);
            setView('admin');
            setAdminSubView('report-details');
        } else {
            alert('No se pudo encontrar el reporte solicitado o no tienes acceso a él.');
        }
    }, [dashboards, pendingApprovalReports]);

    /** Cierra reporte/admin y vuelve al home de catálogo. */
    const goHome = useCallback(() => {
        if (!canLeaveCreator()) return;
        setSelectedReportId(null);
        setSelectedUrlState(null);
        setSelectedReportForDetails(null);
        setAdminSubView('biblioteca');
        setView('user');
    }, []);

    const handleNotificationNavigation = useCallback((notification) => {
        if (notification.module === 'Reports' || notification.module === 'Permissions' || notification.type === 'user_mention_in_comment' || notification.type === 'new_comment_on_report') {
            handleViewReportDetailsById(notification.entityId);
        } else if (notification.module === 'Groups') {
            setView('admin');
            setAdminSubView('grupos');
        }
    }, [handleViewReportDetailsById]);

    const value = {
        view, setView,
        adminSubView, setAdminSubView,
        selectedUrl, setSelectedUrl,
        selectedReportId,
        openReport,
        goHome,
        selectedReportForDetails, setSelectedReportForDetails,
        handleViewReportDetailsById,
        handleNotificationNavigation
    };

    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};